
import { Button, useMediaQuery } from "@relume_io/relume-ui";
import { AnimatePresence, m } from "framer-motion";
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { RxChevronDown } from "react-icons/rx";
import { BiRun } from "react-icons/bi";
import { IoLogoWhatsapp } from "react-icons/io";


// --- Constantes de Animação (Item 013) ---
const mobileMenuVariants = {
  open: { 
    height: "auto",
    opacity: 1,
    display: "block",
    visibility: "visible" as const,
  },
  close: { 
    height: 0,
    opacity: 0,
    visibility: "hidden" as const,
    transitionEnd: {
      display: "none",
    },
  },
};

const dropdownVariants = {
  open: {
    opacity: 1,
    y: 0,
    display: "block",
    visibility: "visible" as const,
  },
  close: {
    opacity: 0,
    y: 5,
    display: "none",
    visibility: "hidden" as const,
  },
};

const iconVariants = {
  rotated: { rotate: 180 },
  initial: { rotate: 0 },
};

const spanVariants1 = {
  open: { translateY: 10, transition: { delay: 0.1 } },
  rotatePhase: { rotate: -45, transition: { delay: 0.2 } },
  closed: { translateY: 0, rotate: 0, transition: { duration: 0.2 } },
};

const spanVariants2 = {
  open: { width: 0, transition: { duration: 0.1 } },
  closed: { width: "1.5rem", transition: { delay: 0.3, duration: 0.2 } },
};

const spanVariants3 = {
  open: { translateY: -10, marginTop: 7, transition: { delay: 0.1 } },
  rotatePhase: { rotate: 45, transition: { delay: 0.2 } },
  closed: { translateY: 0, rotate: 0, transition: { duration: 0.2 } },
};

import { navigationData } from "../../data/navigation";

// --- Subcomponente Reutilizável (Item 014) ---
const NavDropdown = ({ 
  item, 
  index, 
  openDropdown, 
  toggleMobile, 
  openDesktop, 
  closeDesktop, 
  onLinkClick,
  isMobile
}: any) => {
  const isOpen = openDropdown === index;

  // No mobile, "Treinos" vira link direto (Item 015 - Ajuste Mobile)
  if (isMobile && item.title === "Treinos") {
    return (
      <Link
        to="/treinos"
        onClick={onLinkClick}
        className="block py-3 text-md lg:px-4 lg:py-2 lg:text-base"
      >
        {item.title}
      </Link>
    );
  }

  // No mobile, "Mais" exibe apenas seus links sem o título do dropdown
  if (isMobile && item.title === "Mais") {
    return (
      <>
        {item.links.map((link: any) => (
          <Link
            key={link.to}
            to={link.to}
            onClick={onLinkClick}
            className="block py-4 text-md lg:px-4 lg:py-2 lg:text-base"
          >
            {link.label}
          </Link>
        ))}
      </>
    );
  }
  
  return (
    <div 
      className="relative"
      onMouseEnter={() => openDesktop(index)} 
      onMouseLeave={closeDesktop}
    >
      <button
        className="flex w-full items-center justify-between gap-2 py-4 text-left text-md lg:flex-none lg:justify-start lg:px-4 lg:py-2 lg:text-base"
        onClick={() => toggleMobile(index)}
      >
        <span>{item.title}</span>
        <m.span
          variants={iconVariants}
          animate={isOpen ? "rotated" : "initial"}
          transition={{ duration: 0.3 }}
        >
          <RxChevronDown />
        </m.span>
      </button>
      <m.nav
        variants={dropdownVariants}
        initial="close"
        animate={isOpen ? "open" : "close"}
        transition={{ duration: 0.2 }}
        className="ttkk-dropdown-menu bg-background-primary lg:absolute lg:top-full lg:z-50 lg:border lg:border-border-primary lg:p-2 lg:min-w-[240px] lg:shadow-xl lg:rounded-xl"
      >
        {item.links.map((link: any) => (
          <Link
            key={link.to}
            to={link.to}
            onClick={onLinkClick}
            className="block py-4 pl-[5%] text-md hover:bg-neutral-100 rounded-lg transition-colors lg:px-4 lg:py-2 lg:text-base lg:pl-4"
          >
            {link.label}
          </Link>
        ))}
      </m.nav>
    </div>
  );
};

export const Navbar = React.memo(() => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const isMobile = useMediaQuery("(max-width: 991px)");
  const navRef = useRef<HTMLElement>(null);

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const openDesktop = (index: number) => {
    if (!isMobile) setOpenDropdown(index);
  };

  const closeDesktop = () => {
    if (!isMobile) setOpenDropdown(null);
  };

  const toggleMobile = (index: number) => {
    setOpenDropdown((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeMobileMenu();
        setOpenDropdown(null);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        closeMobileMenu();
        setOpenDropdown(null);
      }
    };

    if (isMobileMenuOpen || openDropdown !== null) {
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen, openDropdown]);

  const animateMobileMenuButtonSpan = isMobileMenuOpen
    ? ["open", "rotatePhase"]
    : "closed";

  return (
    <>
      <div className="ttkk-navbar-backdrop" />
      <header 
        ref={navRef}
        className="ttkk-section-nav sticky top-0 z-50 flex w-full items-center border-b border-border-primary bg-background-primary lg:min-h-18 px-4 md:px-8 lg:px-6"
      >
        <nav className="container mx-auto size-full lg:flex lg:items-center lg:justify-between px-4 lg:px-0" aria-label="Navegação principal">
          <div className="ttkk-wrapper-logo flex min-h-14 items-center justify-between md:min-h-16 lg:min-h-full lg:px-0">
            <Link to="/" onClick={closeMobileMenu} className="focus:outline-none focus:ring-2 focus:ring-brand-cyan rounded-lg">
              <img
                src="https://res.cloudinary.com/tatikka/image/upload/v1765152724/Daneluci_-_Logo_Colorido_1_s7kzuf.svg"
                alt="Daneluci Performance - Logo Oficial"
                width={100}
                height={40}
                decoding="async"
              />
            </Link>
            <button
              type="button"
              className="ttkk-btn-menu-mobile -mr-2 flex size-12 flex-col justify-center lg:hidden focus:outline-none focus:ring-2 focus:ring-brand-cyan rounded-lg items-center"
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            >
              <m.span
                className="my-[3px] h-0.5 w-6 bg-black"
                animate={animateMobileMenuButtonSpan}
                variants={spanVariants1}
              />
              <m.span
                className="my-[3px] h-0.5 w-5 bg-black"
                animate={isMobileMenuOpen ? "open" : "closed"}
                variants={spanVariants2}
              />
              <m.span
                className="my-[3px] h-0.5 w-6 bg-black"
                animate={animateMobileMenuButtonSpan}
                variants={spanVariants3}
              />
            </button>
          </div>

          <m.div
            variants={mobileMenuVariants}
            initial="close"
            animate={isMobileMenuOpen ? "open" : "close"}
            transition={{ duration: 0.4 }}
            className="ttkk-nav-group absolute left-4 right-4 top-full mt-2 bg-background-primary shadow-2xl overflow-hidden px-[5%] pt-5 pb-8 rounded-2xl lg:static lg:ml-auto lg:!flex lg:items-center lg:justify-end lg:mt-0 lg:p-0 lg:bg-transparent lg:shadow-none lg:!h-auto lg:overflow-visible lg:!opacity-100 lg:!visible"
          >
            <Link
              to="/"
              onClick={closeMobileMenu}
              className="block py-3 text-md lg:px-4 lg:py-2 lg:text-base hover:text-brand-cyan transition-colors"
            >
              Início
            </Link>

            {navigationData.map((item, index) => (
              <NavDropdown
                key={item.title}
                item={item}
                index={index}
                openDropdown={openDropdown}
                toggleMobile={toggleMobile}
                openDesktop={openDesktop}
                closeDesktop={closeDesktop}
                onLinkClick={closeMobileMenu}
                isMobile={isMobile}
              />
            ))}

            <div className="ttkk-nav-subgroup mt-6 flex flex-col items-center gap-4 lg:mt-0 lg:flex-row lg:ml-4">
              <Link 
                to="/contato" 
                onClick={closeMobileMenu}
                className="w-full lg:w-auto px-6 py-2 border border-black rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-black hover:text-white transition-all focus:outline-none focus:ring-2 focus:ring-brand-cyan"
              >
                <IoLogoWhatsapp /> Contato
              </Link>
              <Link 
                to="/aplicativo-de-treino" 
                onClick={closeMobileMenu}
                className="w-full lg:w-auto px-6 py-2 bg-brand-cyan text-black rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-black hover:text-white transition-all focus:outline-none focus:ring-2 focus:ring-brand-cyan"
              >
                <BiRun /> Aplicativo
              </Link>
            </div>
          </m.div>
        </nav>
      </header>
    </>
  );
});

